import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BookHeart, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { translateOption, useI18n } from "@/lib/i18n";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { pageHead } from "@/lib/seo";

type RecBook = {
  id: string;
  title: string;
  author: string;
  cover_url: string | null;
  category: string;
  reason: string;
};

export const Route = createFileRoute("/recommendations")({
  head: () => pageHead({
    path: "/recommendations",
    title: "Books picked for you — Book Swap",
    description: "Personal picks from books available near you, based on the categories you follow and what neighbors are exchanging.",
  }),
  component: Recommendations,
});

function Recommendations() {
  const { lang, t } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<RecBook[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!user) return;
    setBusy(true);
    try {
      // 1) Categories the user already lists — proxy for their taste.
      const { data: own } = await supabase
        .from("books")
        .select("category")
        .eq("user_id", user.id);

      const counts = new Map<string, number>();
      for (const row of own ?? []) {
        const c = (row as any).category as string | null;
        if (!c) continue;
        counts.set(c, (counts.get(c) ?? 0) + 1);
      }
      const favCats = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);

      // 2) Available books from other users.
      let query = supabase
        .from("books")
        .select("id,title,author,cover_url,category,created_at")
        .eq("status", "available")
        .neq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(60);
      if (favCats.length > 0) query = query.in("category", favCats);

      const { data, error } = await query;
      if (error) throw error;

      let rows = data ?? [];
      // Fallback: no taste signal or no matches → most recent available overall.
      if (rows.length === 0) {
        const { data: recent } = await supabase
          .from("books")
          .select("id,title,author,cover_url,category,created_at")
          .eq("status", "available")
          .neq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(12);
        rows = recent ?? [];
      }

      const merged: RecBook[] = rows.slice(0, 12).map((b: any) => {
        const matchedFav = favCats.includes(b.category);
        const reason = matchedFav
          ? lang === "ar"
            ? `لأنك تشارك كتب ${translateOption(b.category, "ar")}`
            : `Because you share ${b.category} books`
          : lang === "ar"
            ? "أحدث الكتب المتاحة بالقرب منك"
            : "Recently listed by neighbors";
        return {
          id: b.id,
          title: b.title,
          author: b.author,
          cover_url: b.cover_url,
          category: b.category,
          reason,
        };
      });
      setItems(merged);
    } catch (e) {
      console.error(e);
      toast.error(t("recs.error"));
      setItems([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (user) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, lang]);

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [authLoading, user, navigate]);

  if (!authLoading && !user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BookHeart className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">{t("recs.title")}</h1>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy}>
            <RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            {t("recs.refresh")}
          </Button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{t("recs.subtitle")}</p>

        {items === null || busy ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">{t("browse.loading")}</p>
        ) : items.length === 0 ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">{t("recs.no_books")}</p>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {items.map((b) => (
              <Link
                key={b.id}
                to="/book/$id"
                params={{ id: b.id }}
                className="flex gap-4 rounded-2xl bg-card p-4 shadow-[var(--shadow-card)] transition hover:shadow-lg"
              >
                {b.cover_url ? (
                  <img src={b.cover_url} alt={b.title} className="h-28 w-20 rounded object-cover" />
                ) : (
                  <div className="flex h-28 w-20 items-center justify-center rounded bg-muted text-2xl">📖</div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{b.title}</p>
                  <p className="truncate text-sm text-muted-foreground">{b.author}</p>
                  <p className="mt-2 text-xs text-primary-dark">
                    <span className="font-medium">📚 {t("recs.why")}: </span>
                    {b.reason}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
